import React from "react";
import { useParams, Link } from "react-router-dom";
import { EnterpriseNavbar } from "../components/enterprise/EnterpriseNavbar";
import { Footer } from "../components/footer";
import TutorialLayout from "../components/tutorial/TutorialLayout";
import TutorialFigmaFrame from "../components/tutorial/TutorialFigmaFrame";
import useDocumentMeta from "../hooks/useDocumentMeta";
import EN from "../data/en.json";
import AR from "../data/ar.json";
import { getTutorialSection } from "../data/tutorialContent";

/**
 * /guide/:slug — a single walkthrough page. Reads the slug param, looks the
 * section up in tutorialContent, and renders the enterprise shell around the
 * TutorialLayout (hero + breadcrumb + pager), the wireframe frame, and the
 * ported body blocks. Unknown slugs render a graceful "not found" with a link
 * back to /guide.
 */
export default function GuideStory({ lang = "EN", toggleLanguage }) {
  const isArabic = lang === "AR";
  const langKey = isArabic ? "ar" : "en";
  const { slug } = useParams();
  const section = getTutorialSection(slug);

  const data = isArabic ? AR : EN;
  const navActions = data.Enterprise?.navCtas || { primary: "", secondary: "" };
  const navItems = [
    { label: isArabic ? "نظرة عامة" : "Overview", url: "/enterprises" },
    { label: isArabic ? "المحاصيل" : "Crops", url: "/crops" },
    { label: isArabic ? "الدليل" : "Guide", url: "/guide" },
    { label: isArabic ? "المنصة" : "Platform", url: "/platform" },
  ];

  const c = section ? section[langKey] : null;

  useDocumentMeta({
    title: c ? `${c.title} — FarmTopia` : "FarmTopia",
    description: c ? c.blurb : undefined,
  });

  if (!section) {
    return (
      <div
        dir={isArabic ? "rtl" : "ltr"}
        className="min-h-screen bg-gray-50 text-slate-900"
      >
        <EnterpriseNavbar
          lang={lang}
          toggleLanguage={toggleLanguage}
          navItems={navItems}
          actions={navActions}
        />
        <main className="mx-auto max-w-3xl px-4 py-20 text-center">
          <h1 className="text-2xl font-bold text-gray-900">
            {isArabic ? "لم نجد هذه الصفحة" : "Page not found"}
          </h1>
          <Link
            to="/guide"
            className="mt-4 inline-block font-medium text-emerald-700"
          >
            {isArabic ? "تصفّح كل الدليل ←" : "← Browse the full guide"}
          </Link>
        </main>
        <Footer lang={lang} />
      </div>
    );
  }

  // Steps carry a running number; only "step" blocks are counted.
  let stepCounter = 0;

  return (
    <div
      dir={isArabic ? "rtl" : "ltr"}
      className="min-h-screen bg-gray-50 text-slate-900"
    >
      <EnterpriseNavbar
        lang={lang}
        toggleLanguage={toggleLanguage}
        navItems={navItems}
        actions={navActions}
      />

      <TutorialLayout
        lang={lang}
        slug={section.slug}
        eyebrow={c.eyebrow}
        title={c.title}
        description={c.blurb}
      >
        {/* Hero wireframe */}
        <div className="mb-10">
          <TutorialFigmaFrame src={section.image} alt={c.imageAlt || c.title} />
        </div>

        {c.body.map((block, i) => {
          if (block.type === "paragraph") {
            return (
              <p key={i} className="mt-3 leading-relaxed text-gray-700">
                {block.text}
              </p>
            );
          }

          if (block.type === "heading") {
            return (
              <h2
                key={i}
                className="mt-10 text-2xl font-semibold text-gray-900"
              >
                {block.text}
              </h2>
            );
          }

          if (block.type === "step") {
            stepCounter += 1;
            const n = stepCounter;
            return (
              <div
                key={i}
                className={`mt-6 flex gap-5 rounded-xl border border-gray-200 bg-white p-6 shadow-sm ${isArabic ? "flex-row-reverse text-right" : ""}`}
              >
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-700 text-base font-semibold text-white">
                  {n}
                </span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {block.title}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-gray-600">
                    {block.text}
                  </p>
                </div>
              </div>
            );
          }

          if (block.type === "tip") {
            return (
              <div
                key={i}
                className={`mt-4 rounded-xl border border-emerald-100 bg-emerald-50/60 p-5 ${isArabic ? "text-right" : ""}`}
              >
                <h3 className="text-sm font-semibold text-emerald-800">
                  {block.title}
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-gray-600">
                  {block.text}
                </p>
              </div>
            );
          }

          if (block.type === "cta") {
            return (
              <div
                key={i}
                className={`mt-10 flex flex-col gap-3 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-100/50 p-8 sm:flex-row sm:items-center sm:justify-between ${isArabic ? "sm:flex-row-reverse text-right" : ""}`}
              >
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {block.title}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">{block.text}</p>
                </div>
                <Link
                  to="/platform"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-700 px-5 py-3 text-sm font-semibold text-white no-underline shadow transition hover:bg-emerald-800"
                >
                  {block.button}
                </Link>
              </div>
            );
          }

          return null;
        })}
      </TutorialLayout>

      <Footer lang={lang} />
    </div>
  );
}
